import { inject, Injectable, NgZone } from '@angular/core';
import type { IJodit } from 'jodit/types/types';
import { JoditEditorStateService } from './jodit-editor-state.service';

/**
 * JoditEditorLifecycleService - Manages editor initialization and destruction
 *
 * Handles:
 *
 * - Lazy loading of Jodit library
 * - Editor instance creation with event bindings
 * - Proper cleanup on destroy
 * - Running zone optimizations
 *
 * Extracted from component to reduce complexity and improve testability.
 */
@Injectable()
export class JoditEditorLifecycleService {
  private readonly _ngZone = inject(NgZone);
  private readonly _state = inject(JoditEditorStateService);

  /** Initialize Jodit editor instance */
  async initializeEditor(
    container: HTMLElement,
    config: IJodit['options'],
    callbacks: {
      onChange: (value: string) => void;
      onFocus: () => void;
      onBlur: () => void;
      onReady: (editor: IJodit) => void;
      onAfterSetMode: () => void;
      onAfterPaste: () => void;
    }
  ): Promise<void> {
    try {
      // Lazy load JODIT
      const { Jodit } = await import('jodit');

      // Create editor instance outside Angular zone for performance
      this._ngZone.runOutsideAngular(() => {
        // Build config without spreading events to avoid "Type instantiation is excessively deep" error
        // Use type assertion to bypass deep type checking on Jodit's complex config type
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const editorConfig: any = { ...config };
        editorConfig.events = {
          // Change event
          change: (newValue: string): void => {
            this._ngZone.run(() => {
              callbacks.onChange(newValue);
            });
          },

          // Focus event
          focus: (): void => {
            this._ngZone.run(() => {
              callbacks.onFocus();
            });
          },

          // Blur event
          blur: (): void => {
            this._ngZone.run(() => {
              callbacks.onBlur();
            });
          },

          // After init
          afterInit: (joditInstance: IJodit): void => {
            // Set the editor instance in state BEFORE calling onReady.
            // Jodit fires afterInit synchronously during Jodit.make(), so
            // this._state.setEditor() (called after Jodit.make() returns) has
            // not run yet. Without this, state.editor() is null when onReady
            // triggers setValue() calls from parent components (e.g. rich-text-editor
            // onEditorCreated), causing content and body-style injection to be skipped.
            this._state.setEditor(joditInstance);
            this._ngZone.run(() => {
              callbacks.onReady(joditInstance);
            });
          },

          // After mode switch (WYSIWYG ↔ Source)
          afterSetMode: (): void => {
            this._ngZone.run(() => {
              callbacks.onAfterSetMode();
            });
          },

          // After paste (handles full-document pastes)
          afterPaste: (): void => {
            this._ngZone.run(() => {
              callbacks.onAfterPaste();
            });
          },
        };

        const editorInstance = Jodit.make(container, editorConfig);

        this._state.setEditor(editorInstance);
      });
    } catch (error) {
      console.error('[HxJoditEditor] Failed to initialize editor:', error);
      throw error;
    }
  }

  /** Destroy editor instance and clean up resources */
  destroyEditor(): void {
    const editor = this._state.editor();
    if (editor) {
      try {
        editor.destruct();
      } catch (error) {
        console.error('[HxJoditEditor] Error destroying editor:', error);
      }
      this._state.setEditor(null);
      this._state.setEditorReady(false);
    }
  }
}
